import type { WireOverview } from '@logistics/contracts';
import { clockTime, monthDay, versusYesterday, whole } from '../lib/format';
import type { Route } from '../shell/routes';

export interface StatTile {
  readonly label: string;
  readonly value: string;
  readonly sub: string;
  readonly subColor: string;
  readonly to: Route;
}

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

/** The four tiles under the hero (design L990–995), from `GET /overview`. */
export function statTiles(o: WireOverview): StatTile[] {
  const { last24h, prev24h } = o.retrievals;
  return [
    {
      label: 'SOURCES',
      value: whole(o.sources.total),
      sub: o.sources.errors > 0 ? `${o.sources.errors} error` : 'all healthy',
      subColor: o.sources.errors > 0 ? 'var(--bad)' : 'var(--ok)',
      to: { page: 'sources' },
    },
    {
      label: 'ALLOWED USERS',
      value: whole(o.users.active),
      sub: `${o.users.disabled} disabled`,
      subColor: 'var(--mute)',
      to: { page: 'users' },
    },
    {
      label: 'RETRIEVALS · 24H',
      value: whole(last24h),
      sub: versusYesterday(last24h, prev24h),
      subColor: prev24h > 0 && last24h >= prev24h ? 'var(--ok)' : 'var(--mute)',
      to: { page: 'activity', query: { type: 'retrieve' } },
    },
    {
      label: 'BLOCKED SENDERS',
      value: whole(o.blocked24h),
      sub: 'last 24h',
      subColor: 'var(--mute)',
      to: { page: 'activity', query: { type: 'blocked' } },
    },
  ];
}

/** "09:41" when it was in the last day, "Sep 17" before that, a dash for never. */
export function lastReply(iso: string | undefined, now: Date): string {
  if (iso === undefined) return '—';
  const within = now.getTime() - new Date(iso).getTime() < 86_400_000;
  return within ? clockTime(iso) : monthDay(iso);
}

/** The hero's status line (design L1044). */
export function heroLine(o: WireOverview, now: Date): string {
  const errors = o.sources.errors;
  return [
    `${o.sources.total} ${plural(o.sources.total, 'source', 'sources')} indexed`,
    `${o.users.active} ${plural(o.users.active, 'user', 'users')} allowed`,
    `last reply ${lastReply(o.lastReplyAt, now)}`,
    errors > 0
      ? `${errors} ${plural(errors, 'source needs', 'sources need')} attention`
      : 'no alerts',
  ].join(' · ');
}

/** The headline: nominal, or how many sources are failing. */
export function headline(o: WireOverview): string {
  const errors = o.sources.errors;
  if (errors === 0) return 'All systems nominal.';
  return `${errors} ${plural(errors, 'source needs', 'sources need')} attention.`;
}
